import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import moment from 'moment';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAsyncEffect } from 'rooks';
import { ContainerWithHTML } from '@/core/ContainerWithHTML.atom';
import utilities from '@/core/utilities';
import { KpiLabelWithDescription } from '@/impactReports/KpiLabelWithDescription.atom';
import { SelectedProjectDisplayAndLink } from '@/impactReports/SelectedProjectDisplayAndLink.atom';
import { SelectedWorksheetDisplayAndLink } from '@/impactReports/SelectedWorksheetDisplayAndLink.atom';
import { UserNameCellRenderer } from '@/impactReports/cellRenderers/UserNameCellRenderer.atom';
import {
  IMPACT_OWNER_FIRSTNAME,
  IMPACT_OWNER_LASTNAME,
  IMPACT_REPORT_LABEL_ID,
  REVIEWED_BY_FIRSTNAME,
  REVIEWED_BY_LASTNAME,
  TRACK_IMPACT,
  VIEW_IMPACT_QUERY_PARAM,
} from '@/impactReports/impactReport.types';
import { getImpactStageIcon, replaceLinks } from '@/impactReports/impactReport.utilities';
import {
  getCategoryDisplayString,
  getCustomCategoryTitle,
  getImpactTypeDisplayString,
  getKPIDisplayName,
  getKPIExpectedAnnualColumnName,
  getKPIUnit,
  getStageDisplayString,
} from '@/impactReports/impactReportDisplay.utilities';
import {
  useImpactReportActions,
  useImpactReportExpectedAnnualKpiVisibility,
  useImpactReportKpiColumns,
  useImpactReportTableId,
} from '@/impactReports/impactReports.store';
import { decorateMaterializedTableWithImpactValue, getValueFormatter } from '@/impactReports/tiles/table.utilities';
import { getViewWorksheetLink } from '@/main/routing.utilities';
import { deleteTableRows } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { doTrack } from '@/track/track.service';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { sqContextApi } from '@seeqdev/api/endpoint/ContextApi';
import { SeeqNames } from '@seeqdev/constants';
import { faLink } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Button, Icon, Pill } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { UserNameCellProps } from '@/impactReports/cellRenderers/UserNameCellRenderer.atom';
import type { ImpactReportCategory, ImpactReportUseCase, ImpactType, Stages } from '@/impactReports/impactReport.types';
import type { ICellRendererParams } from '@seeqdev/ag/grid';

interface ImpactReportDetailsProps extends ImpactReportUseCase {
  categories: ImpactReportCategory[] | undefined;
  [key: string]: unknown;
  onClose: () => void;
  onEdit?: () => void;
  editable?: boolean;
}

interface ColumnItem {
  title: ReactNode;
  value: string | undefined;
  expectedAnnual?: string | undefined;
}

export const ImpactReportDetailHeader = (props: ImpactReportUseCase) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const assignedWorkbookId = props[SeeqNames.ImpactReport.AssignedToWorkbook];
  const assignedWorksheetId = props[SeeqNames.ImpactReport.AssignedToWorksheet];
  const isProject = props[SeeqNames.ImpactReport.ImpactBasedOn] === SeeqNames.Types.Project;
  const isWorksheetRoute = pathname.includes('/worksheet/') || pathname.startsWith('/view/');
  const showViewAllImpactReportsLink = isWorksheetRoute && !pathname.startsWith('/impact');
  const params = {
    value: {
      first: props[IMPACT_OWNER_FIRSTNAME]!,
      last: props[IMPACT_OWNER_LASTNAME]!,
    },
  } as ICellRendererParams<ImpactReportUseCase, UserNameCellProps>;
  const location = props[SeeqNames.ImpactReport.Location] || null;
  const locationObj = location !== null ? JSON.parse(location) : null;
  const locationDisplay = locationObj ? locationObj.label : undefined;
  const site = props[SeeqNames.ImpactReport.ImpactSite] || undefined;
  const locationAndSite = _.compact([locationDisplay, site]).join(', ');
  const viewWorksheetLink =
    assignedWorkbookId && assignedWorksheetId
      ? getViewWorksheetLink(assignedWorkbookId, assignedWorksheetId, undefined, undefined, true)
      : undefined;
  const viewImpactReportLink = viewWorksheetLink ? `${viewWorksheetLink}?${VIEW_IMPACT_QUERY_PARAM}` : undefined;
  const copyReportLink = async () => {
    if (!viewImpactReportLink) return;
    doTrack(TRACK_IMPACT, 'Copy Impact Report link', 'from Impact Report detail modal');
    const copied = await utilities.copyTextToClipboard(viewImpactReportLink);
    if (copied) {
      successToast({ messageKey: 'COPY_URL.SUCCESS' });
    }
  };
  return (
    <div {...stylex.props(styles.header)}>
      <div {...stylex.props(styles.headerTop)}>
        <div {...stylex.props(styles.column)}>
          <UserNameCellRenderer {...params} />
        </div>
        <div {...stylex.props(styles.headerActions)}>
          <Button
            label={t('IMPACT_REPORT.COPY_REPORT_LINK')}
            variant="outline"
            size="sm"
            icon={faLink}
            testId="copyImpactReportLink"
            disabled={!viewImpactReportLink}
            onClick={copyReportLink}
          />
        </div>
      </div>
      <h2 {...stylex.props(styles.title)}>{props[SeeqNames.ImpactReport.ImpactName]}</h2>
      {locationAndSite}
      <br />
      {isProject && assignedWorkbookId ? (
        <SelectedProjectDisplayAndLink projectId={assignedWorkbookId} />
      ) : assignedWorkbookId && assignedWorksheetId ? (
        <div {...stylex.props(styles.links)}>
          <SelectedWorksheetDisplayAndLink
            worksheetId={assignedWorksheetId}
            workbookId={assignedWorkbookId}
            extraClassNames={stylex.props(styles.worksheetLink).className}
          />
          {showViewAllImpactReportsLink && (
            <>
              <span {...stylex.props(styles.linkDivider)} />
              <Link
                to="/impact"
                data-testid="viewAllImpactReportsLink"
                onClick={() => doTrack(TRACK_IMPACT, 'View all Impact Reports', 'from Impact Report detail modal')}
                {...stylex.props(styles.viewAll)}>
                <span>{t('IMPACT_REPORT.VIEW_ALL')}</span>
              </Link>
            </>
          )}
        </div>
      ) : null}
      <div {...stylex.props(styles.stage)}>
        <Pill variant="selection" xstyle={styles.stagePill}>
          <Icon
            type="theme"
            testId="stageIcon"
            icon={getImpactStageIcon(props[SeeqNames.ImpactReport.ImpactStatus] as Stages)}
          />
          {getStageDisplayString(props[SeeqNames.ImpactReport.ImpactStatus] as Stages)}
        </Pill>
      </div>
    </div>
  );
};
export const ImpactReportDetail = (props: ImpactReportDetailsProps) => {
  const { t } = useTranslation();
  const { setForceTableReload } = useImpactReportActions();
  const { categories, onClose, onEdit } = props;
  const tableId = useImpactReportTableId();
  const kpiColumns = useImpactReportKpiColumns();
  const expectedAnnualKpiVisibility = useImpactReportExpectedAnnualKpiVisibility();
  const [kpiDisplays, setKpiDisplays] = useState<ColumnItem[]>([]);

  const deleteUseCase = async () => {
    try {
      const itemId = props[SeeqNames.MaterializedTables.ItemIdColumn];
      if (!itemId || !tableId) {
        throw new Error('Missing ids required to delete impact report');
      }
      const contextLabelIds =
        categories
          ?.map((category) => {
            const categoryId = category.category.id;
            return categoryId ? props[`${categoryId}_${IMPACT_REPORT_LABEL_ID}`] : undefined;
          })
          .filter((contextLabelId): contextLabelId is string => Boolean(contextLabelId)) ?? [];

      await Promise.all(
        contextLabelIds.map((contextLabelId) => sqContextApi.deleteContextLabel({ itemId, labelId: contextLabelId })),
      );

      await deleteTableRows(tableId, [
        {
          itemId,
          datumId: props[SeeqNames.MaterializedTables.DatumIdColumn],
        },
      ]);
      setForceTableReload(true);
      onClose();
    } catch (e) {
      errorToast({ messageKey: 'IMPACT_REPORT.NO_DELETE' });
    }
  };

  useAsyncEffect(async () => {
    const data = await decorateMaterializedTableWithImpactValue(
      [props],
      kpiColumns ?? [],
      moment().subtract(1, 'year').toISOString(),
      moment().toISOString(),
    );
    const kpiWithValue = kpiColumns.map((item) => {
      const unit = getKPIUnit(item.columnName);
      const formatterFn = getValueFormatter(unit);
      const value = data[0][item.columnName];
      const expectedAnnualColumnName = getKPIExpectedAnnualColumnName(item.columnName);
      const expectedAnnualRaw = props[expectedAnnualColumnName];
      const expectedAnnual =
        typeof expectedAnnualRaw === 'string' || typeof expectedAnnualRaw === 'number' ? expectedAnnualRaw : undefined;
      const expectedAnnualNumber = Number(expectedAnnual);
      const expectedAnnualDisplay =
        expectedAnnual === undefined || expectedAnnual === ''
          ? '-'
          : Number.isFinite(expectedAnnualNumber)
            ? formatterFn(expectedAnnualNumber, unit)
            : String(expectedAnnual);
      return {
        title: <KpiLabelWithDescription label={getKPIDisplayName(item.columnName)} description={item.description} />,
        value: !value ? '-' : formatterFn(value, unit),
        expectedAnnual: expectedAnnualKpiVisibility[item.columnName] === true ? expectedAnnualDisplay : undefined,
      };
    });
    setKpiDisplays(kpiWithValue ?? []);
  }, [props, kpiColumns, expectedAnnualKpiVisibility]);

  const categoryDisplays =
    categories?.map((item) => {
      const categoryValue = item.category?.id ? props[item.category.id] : undefined;
      const display =
        typeof categoryValue === 'string' ? getCategoryDisplayString(categoryValue, item.options) : undefined;
      return { title: getCustomCategoryTitle(item), value: display };
    }) ?? [];

  const reviewedByName = `${props[REVIEWED_BY_FIRSTNAME] ?? ''} ${props[REVIEWED_BY_LASTNAME] ?? ''}`.trim();
  const reviewDate = props[SeeqNames.ImpactReport.ReviewDate];
  const reviewDisplays = [
    props[SeeqNames.ImpactReport.ReviewComment]
      ? {
          title: t('IMPACT_REPORT.REVIEW_COMMENT'),
          value: props[SeeqNames.ImpactReport.ReviewComment],
        }
      : null,
    reviewDate
      ? {
          title: t('IMPACT_REPORT.REVIEW_DATE'),
          value: moment(reviewDate).format('l'),
        }
      : null,
    reviewedByName || props[SeeqNames.ImpactReport.ReviewedBy]
      ? {
          title: t('IMPACT_REPORT.REVIEWED_BY'),
          value: reviewedByName || props[SeeqNames.ImpactReport.ReviewedBy],
        }
      : null,
  ].filter(Boolean) as ColumnItem[];

  const columns = [
    {
      title: t('IMPACT_REPORT.TABLE_HEADERS.TYPE'),
      value: getImpactTypeDisplayString(props[SeeqNames.ImpactReport.ImpactSavingsType] as ImpactType),
    },
    ...categoryDisplays,
    ...reviewDisplays,
  ];

  return (
    <div {...stylex.props(styles.body)}>
      <div {...stylex.props(styles.content)}>
        <div {...stylex.props(styles.description)}>
          <ContainerWithHTML
            content={replaceLinks(
              props[SeeqNames.ImpactReport.ImpactDescription] ?? '',
              props[SeeqNames.ImpactReport.AssignedToWorkbook],
              props[SeeqNames.ImpactReport.AssignedToWorksheet],
            )}
            isBlock
          />
        </div>

        <div {...stylex.props(styles.section)}>
          <h4>{t('IMPACT_REPORT.TOTAL_IMPACT_PAST_YEAR')}</h4>
          {kpiDisplays
            ?.reduce<ColumnItem[][]>((rows, item, index) => {
              if (index % 2 === 0) {
                rows.push(kpiDisplays?.slice(index, index + 2));
              }
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <div {...stylex.props(styles.fields)} key={rowIndex}>
                {row.map((item, colIndex) => {
                  return (
                    <div {...stylex.props(styles.kpi)} key={colIndex}>
                      <div {...stylex.props(styles.fieldTitle)}>{item.title}</div>
                      <div {...stylex.props(styles.value)}>{item.value}</div>
                      {item.expectedAnnual !== undefined && (
                        <div {...stylex.props(styles.expectedAnnual)}>
                          {t('IMPACT_REPORT.EXPECTED_ANNUAL_SAVINGS')}: {item.expectedAnnual}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
        <hr {...stylex.props(styles.divider)} />
        <div {...stylex.props(styles.section)}>
          {columns
            ?.reduce<ColumnItem[][]>((rows, item, index) => {
              if (index % 2 === 0) {
                rows.push(columns?.slice(index, index + 2));
              }
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <div {...stylex.props(styles.fields)} key={rowIndex}>
                {row.map((item, colIndex) => {
                  return (
                    <div {...stylex.props(styles.field)} key={colIndex}>
                      <div {...stylex.props(styles.fieldTitle)}>{item.title}</div>
                      <p>{item.value}</p>
                    </div>
                  );
                })}
              </div>
            ))}
        </div>
      </div>

      <div {...stylex.props(styles.footer)}>
        <div {...stylex.props(styles.footerStart)}>
          {onEdit && <Button label={t('DELETE')} onClick={deleteUseCase} variant="danger" testId="deleteImpact" />}
        </div>
        <Button testId="closeImpactDetail" label={t('CLOSE')} onClick={onClose} xstyle={styles.closeButton} />
        {onEdit && <Button label={t('EDIT')} onClick={onEdit} variant="theme" testId="editImpact" />}
      </div>
    </div>
  );
};

const styles = stylex.create({
  worksheetLink: { textDecoration: 'underline' },
  header: { display: 'flex', width: '100%', flexDirection: 'column' },
  headerTop: {
    marginBottom: '0.5rem',
    display: 'flex',
    flex: '1',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  column: { display: 'flex', flexDirection: 'column' },
  headerActions: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' },
  title: { marginBottom: '0.625rem' },
  links: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' },
  linkDivider: { height: '1rem', width: 1, backgroundColor: colors.divider },
  viewAll: {
    display: 'inline-flex',
    width: 'fit-content',
    alignItems: 'center',
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 500,
    color: colors.link,
    textDecoration: 'underline',
  },
  stage: {
    marginTop: '0.5rem',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.5rem',
  },
  body: { height: '100%', overflowY: 'auto', padding: 25 },
  content: { display: 'flex', flex: '1', flexDirection: 'column', overflow: 'hidden' },
  description: { marginBottom: '0.625rem', display: 'flex', flexDirection: 'column' },
  section: { marginLeft: '0.625rem', width: '100%' },
  fields: { display: 'flex', flexWrap: 'wrap', columnGap: '1.5rem' },
  kpi: { minWidth: 0, flex: '1', paddingBottom: '0.625rem' },
  fieldTitle: { marginBottom: 1, fontSize: '1.125rem', lineHeight: '1.75rem' },
  value: { marginBottom: 0 },
  expectedAnnual: { marginTop: '0.125rem', fontSize: '0.875rem', lineHeight: '1.25rem', color: colors.secondaryText },
  divider: { marginBottom: '0.375rem' },
  field: { minWidth: 0, flex: '1' },
  footer: { marginTop: 25, display: 'flex', flex: '1', flexDirection: 'row', overflow: 'hidden' },
  footerStart: { display: 'flex', flex: '1', flexDirection: 'row', overflow: 'hidden' },
  stagePill: { gap: '0.25rem' },
  closeButton: { marginRight: '0.625rem' },
});
